import React from 'react';
import { Alert } from '@mui/material';
import { TeamSummary, UserSummaryExternal, SlaEscalationStatus } from '@askporter/client-grieg-lyric';
import { fullName, getInitialsFromLongName } from '@askporter/utils';
import { Chip } from '../../Chip';
import { getTaskEscalationAlertProps } from './utils';

type EscalatedToUnion =
  | {
      escalatedToType: 'user';
      escalatedTo: UserSummaryExternal;
    }
  | {
      escalatedToType: 'team';
      escalatedTo: TeamSummary;
    }
  | {
      escalatedToType: 'none';
      escalatedTo: null;
    };

export type TaskEscalationAlertProps = {
  t: (key: string) => string;
  escalationStatus: SlaEscalationStatus;
  escalatedUser: UserSummaryExternal | undefined;
  escalatedTeam: TeamSummary | undefined;
};

export const TaskEscalationAlert: React.FC<TaskEscalationAlertProps> = ({
  t,
  escalationStatus,
  escalatedTeam,
  escalatedUser,
}) => {
  const taskEscalationAlertProps = getTaskEscalationAlertProps({ escalationStatus, t });

  if (!taskEscalationAlertProps) return null;

  const { escalatedTo, escalatedToType }: EscalatedToUnion = {
    ...(escalatedUser?.uid
      ? {
          escalatedTo: escalatedUser,
          escalatedToType: 'user',
        }
      : escalatedTeam?.uid
      ? {
          escalatedTo: escalatedTeam,
          escalatedToType: 'team',
        }
      : {
          escalatedTo: null,
          escalatedToType: 'none',
        }),
  };

  const { escalationSeverity, escalationIcon, escalationLevelText } = taskEscalationAlertProps || {};

  return (
    <Alert sx={{ borderRadius: '8px' }} severity={escalationSeverity} icon={false}>
      <Chip
        startAdornment={{ mode: 'icon', icon: escalationIcon }}
        color={escalationSeverity}
        label={escalationLevelText}
        variant="outlined"
      />
      {escalatedTo && (
        <Chip
          sx={{
            marginLeft: 3,
          }}
          data-testid="escalated-to-chip"
          startAdornment={{
            mode: 'avatar',
            src: escalatedToType === 'user' ? escalatedTo?.profilePhoto?.filePath : undefined,
            fallbackText: getInitialsFromLongName(
              escalatedToType === 'user'
                ? fullName(escalatedTo?.givenName, escalatedTo?.familyName)
                : escalatedTo?.name,
            ),
          }}
          color={escalationSeverity}
          label={
            escalatedToType === 'user' ? fullName(escalatedTo?.givenName, escalatedTo?.familyName) : escalatedTo?.name
          }
          variant="outlined"
        />
      )}
    </Alert>
  );
};
